import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Badge, Form, Tooltip } from '@syneto/compass-react';
import _ from 'lodash';
import Preloader from '../../../../../../components/preloader';
import moment from 'moment';
import { getCompanyServices } from '../../../../../../services/companies';
import { getPartnerPreviewParticipations, createPartnerPreviewParticipation, updatePartnerPreviewParticipation } from '../../../../../../services/partnerPreview';
import { pollJobUntilTerminal } from '../../../../../../services/jobs';
import PartnerPreviewConfirmationModal from './PartnerPreviewConfirmationModal';

// `notification` is what the confirmation modal decided: `{ recipients }` when the customer is notified, `null`
// when Notify customer is cleared. The modal owns that rule; this only turns it into the request body.
const buildNotificationPayload = (enabled, notification) => {
	if (!enabled) {
		return {};
	}

	return notification
		? { notifyCustomer: true, notificationRecipients: notification.recipients }
		: { notifyCustomer: false };
};

const TabServices = (props) => {
	const { companyId, loggedInUserId, pushDangerNotification, pushSuccessfulNotification } = props;

	const [loadingCompanyServices, setLoadingCompanyServices] = useState(false);
	const [rasServiceDetails, setRasServiceDetails] = useState(null);
	const [partnerPreviewEnabled, setPartnerPreviewEnabled] = useState(false);
	const [partnerPreviewParticipationId, setPartnerPreviewParticipationId] = useState(null);
	const [partnerPreviewSaving, setPartnerPreviewSaving] = useState(false);
	const [showPartnerPreviewConfirmationModal, setShowPartnerPreviewConfirmationModal] = useState(false);

	const partnerPreviewPollAbortControllerRef = useRef(null);

	useEffect(() => {
		loadCompanyServices();
		loadPartnerPreviewParticipation();

		return () => {
			partnerPreviewPollAbortControllerRef.current?.abort();
		};
	}, []);

	const loadCompanyServices = async () => {
		try {
			setLoadingCompanyServices(true);

			const companyServices = await getCompanyServices(companyId);
			const rasService = companyServices.find((service) => { return service.name === 'Remote Access Service'; });

			if (rasService) {
				setRasServiceDetails({
					rasAvailableSeats: _.find(rasService.service_properties, { 'name': 'availableSeats' })?.value,
					rasStartingPeriod: _.find(rasService.service_properties, { 'name': 'startingPeriod' })?.value,
					rasEndingPeriod: _.find(rasService.service_properties, { 'name': 'endingPeriod' })?.value
				});
			} else {
				setRasServiceDetails(null);
			}

			setLoadingCompanyServices(false);
		} catch (e) {
			pushDangerNotification('Company services cannot be loaded');
		}
	};

	const loadPartnerPreviewParticipation = async () => {
		try {
			const response = await getPartnerPreviewParticipations(companyId);
			const participations = response.data;
			
			if (participations && participations.length > 0) {
				const participation = participations[0];
				setPartnerPreviewEnabled(participation.enabled);
				setPartnerPreviewParticipationId(participation.id);
			} else {
				setPartnerPreviewEnabled(false);
				setPartnerPreviewParticipationId(null);
			}
		} catch (error) {
			pushDangerNotification('Partner Preview participation status cannot be loaded');
			setPartnerPreviewEnabled(false);
			setPartnerPreviewParticipationId(null);
		}
	};

	const handlePartnerPreviewToggle = () => {
		const newValue = !partnerPreviewEnabled;

		if (newValue) {
			setShowPartnerPreviewConfirmationModal(true);
			return;
		}

		submitPartnerPreviewChange(newValue);
	};

	const handleConfirmPartnerPreviewChange = (notification) => {
		setShowPartnerPreviewConfirmationModal(false);
		submitPartnerPreviewChange(true, notification);
	};

	const handleCancelPartnerPreviewChange = () => {
		setShowPartnerPreviewConfirmationModal(false);
	};

	const submitPartnerPreviewChange = async (enabled, notification) => {
		setPartnerPreviewSaving(true);

		const abortController = new AbortController();
		partnerPreviewPollAbortControllerRef.current = abortController;
		const notifyCustomerPayload = buildNotificationPayload(enabled, notification);

		try {
			let response;

			if (partnerPreviewParticipationId) {
				response = await updatePartnerPreviewParticipation(partnerPreviewParticipationId, {
					enabled: enabled,
					userId: loggedInUserId,
					...notifyCustomerPayload
				});
			} else {
				response = await createPartnerPreviewParticipation({
					companyId: companyId,
					enabled: enabled,
					userId: loggedInUserId,
					...notifyCustomerPayload
				});
			}

			const jobId = response?.data?.jobId;
			const jobResult = await pollJobUntilTerminal(jobId, { signal: abortController.signal });

			if (jobResult.status.toLowerCase() === 'succeeded') {
				setPartnerPreviewEnabled(enabled);
				// The created participation's id is carried in the job's `result` payload. The job envelope's
				// own `id` is the command message id, which is unrelated to the participation.
				const createdParticipationId = jobResult.result?.id;

				if (!partnerPreviewParticipationId && createdParticipationId) {
					setPartnerPreviewParticipationId(createdParticipationId);
				}
				pushSuccessfulNotification('Partner Preview Program participation updated.');
			} else {
				pushDangerNotification('Partner Preview Program participation update failed.');
			}
		} catch (error) {
			if (error?.name === 'AbortError') { return; }
			pushDangerNotification('Partner Preview Program participation update failed.');
		} finally {
			if (!abortController.signal.aborted) {
				setPartnerPreviewSaving(false);
			}
		}
	};

	const renderAvailability = () => {
		const currentDate = moment().format('YYYY-MM-DD');

		if ( rasServiceDetails?.rasEndingPeriod ) {
			return moment(rasServiceDetails.rasEndingPeriod).diff(currentDate, 'days') > 0
				? <Badge appearance="success">Active</Badge>
				: <Badge appearance="danger">Expired</Badge>;
		}

		return null;
	};

	if ( loadingCompanyServices ) { return <Preloader dataIsLoading={ loadingCompanyServices }/>; }

	return (
		<div className="container mt-3 ps-0 pf-0">
			<div className="row">
				{rasServiceDetails ? (
					<div className="col-6">
						<h5>RAS Licensing</h5>

						<Row className="mt-3">
							<Col><b>Status</b></Col>
							<Col>{ renderAvailability() }</Col>
						</Row>

						<Row className="mt-2">
							<Col><b>Number of Licensed Seats</b></Col>
							<Col>{ rasServiceDetails?.rasAvailableSeats }</Col>
						</Row>

						<Row className="mt-2">
							<Col><b>Activation date</b></Col>
							<Col>{ rasServiceDetails?.rasStartingPeriod ?
								moment(rasServiceDetails.rasStartingPeriod).format('DD/MM/YYYY') : null }</Col>
						</Row>

						<Row className="mt-2">
							<Col><b>Expiration date</b></Col>
							<Col>{ rasServiceDetails?.rasEndingPeriod ?
								moment(rasServiceDetails.rasEndingPeriod).format('DD/MM/YYYY') : null }</Col>
						</Row>
					</div>
				) : (
					<div className="col-12">
						<p className="text-muted">Currently, the remote access service for this account is not active.</p>
					</div>
				)}

				<div className="col-6">
					<h5>Partner Preview Program
						<Tooltip
							text="When enabled, end users for this company can enable access to Preview updates for each registered machine from the Inventory side pane in Central Hub."
							placement="bottom"
						>
							<i className="fa fa-question ms-1" />
						</Tooltip>
					</h5>
					<Form.Group className={`switch ${partnerPreviewSaving ? 'disabled' : ''} mb-3 d-flex gap-2`}>
						<Form.Label>Disabled</Form.Label>
						<Form.Switch checked={partnerPreviewEnabled} onChange={handlePartnerPreviewToggle} disabled={partnerPreviewSaving} />
						<Form.Label>Enabled</Form.Label>
					</Form.Group>

					{showPartnerPreviewConfirmationModal && (
						<PartnerPreviewConfirmationModal
							companyId={companyId}
							onConfirm={handleConfirmPartnerPreviewChange}
							onHide={handleCancelPartnerPreviewChange}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default TabServices;
